<template>
<div class="modal" :class="{ 'is-active': active }">
  <div class="modal-background">
  </div>

  <div class="modal-content">
    <ai-banner v-if="isAi"></ai-banner>
    <header class="modal-header">
      <h1 class="title is-4">{{ title  }}</h1>
      <a class="modal-header-close" @click="close">Close</a>
    </header>
    <div class="main-content">

      <banner  :active.sync="isBannerActive" :error="isError" :title="bannerTitle"></banner>
      <slot></slot>
    </div>
    <footer class="modal-footer" v-if="footer">
      <slot name="footer"></slot>
    </footer>
    <footer class="modal-footer" v-if="button || destructive || clear || info">
      <button class="button is-primary is-fullwidth" v-if="button"
        :class="{ 'is-loading': loading }"
        @click="action"
        :disabled="!buttonEnabled">
        {{ button }}
      </button>
      <button class="button is-primary is-fullwidth" v-if="info"
        :class="{ 'is-loading': infoLoading }"
        @click="onInfo">
        {{ info }}
      </button>
      <button class="button is-danger is-fullwidth" v-if="destructive"
        :class="{ 'is-loading': destroyLoading }"
        @click="destroy">
        {{ destructive }}
      </button>
      <button class="button is-primary is-fullwidth is-inverted" v-if="clear"
        :class="{ 'is-loading': clearLoading }"
        @click="onClear">
        {{ clear }}
      </button>
    </footer>
  </div>
</div>
</template>

<script>
import Banner from '@/components/general/banner'
import AiBanner from './jobs/al-banner.vue'

export default {
  components: {
    AiBanner,
    Banner
  },
  props: {
    active: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    button: {
      type: String,
      default: null
    },
    buttonEnabled: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    destructive: {
      type: String,
      default: null
    },
    info: {
      type: String,
      default: null
    },
    clear: {
      type: String,
      default: null
    },
    infoLoading: {
      type: Boolean,
      default: false
    },
    destroyLoading: {
      type: Boolean,
      default: false
    },
    clearLoading: {
      type: Boolean,
      default: false
    },
    footer: {
      type: Boolean,
      default: false
    },
    message: {
      type: String,
      default: null
    },
    error: {
      type: String,
      default: null
    },
    isAi: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isBannerActive: {
      get () {
        return this.error !== null || this.message !== null
      },
      set (val) {
        this.$emit('update:error', null)
        this.$emit('update:message', null)
      }
    },
    isError () {
      return this.error !== null
    },
    bannerTitle () {
      return this.error ? this.error : this.message
    }
  },
  watch: {
    active (newValue, oldValue) {
      if (newValue && !oldValue) {
        document.documentElement.classList.add('is-clipped')
      } else if (oldValue && !newValue) {
        document.documentElement.classList.remove('is-clipped')
      }
    }
  },
  filters: {
    uppercased (value) {
      if (!value) { return '' }
      value = value.toString()
      return value.toUpperCase()
    }
  },
  methods: {
    close () {
      this.$emit('update:active', false)
    },
    action () {
      this.$emit('action')
    },
    destroy () {
      this.$emit('destroy')
    },
    onClear () {
      this.$emit('clear')
    },
    onInfo () {
      this.$emit('info')
    }
  }
}
</script>

<style lang="scss" scoped>

@import '~@/styles/defaults.scss';

.modal-header {
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
  background: $background-color;
  min-height: $header-height;
  height: $header-height;
  border: solid 1px #dddddd;
  padding: 16px;
}

.modal-header h1.title {
  margin-bottom: 0px;
  font-weight: bold;
}

.modal-content {
  margin: 0;
  min-width: 240px;
  width: 100%;
  display: flex;
  background: $background-color;
  border-bottom-left-radius: 4px;
  border-top-left-radius: 4px;
  flex-direction: column;
  max-height: calc(100vh);
  height: 100vh;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);
  overflow-x: hidden;
}

@media screen and (min-width: $tablet) {
  .modal-content {
    min-width: 30rem;
    width: 50%;
  }
}

.modal {
  flex-direction: column;
  align-items: flex-end;
  overflow: hidden;
}

.modal-header-close {
  position: relative;
  height: 100%;
  width: 46px;
  display: inline-block;
  overflow: hidden;
  text-indent: 100%;
  white-space: nowrap;
}

.modal-header-close::before, .modal-header-close::after {
  /* close icon created in CSS */
  content: '';
  position: absolute;
  top: 12px;
  left: 25px;
  height: 2.5px;
  width: 24px;
  background-color: #ccc;
  backface-visibility: hidden;
}

.modal-header-close::before {
  transform: rotate(45deg);
}

.modal-header-close::after {
  transform: rotate(-45deg);
}

.modal-header-close:hover {
}

.modal-header-close:hover::before, .modal-header-close:hover::after {
  transition: transform 0.3s;
}

.modal-header-close:hover::before {
  transform: rotate(220deg);
}

.modal-header-close:hover::after {
  transform: rotate(135deg);
}

.main-content {
  padding: 1.5rem 1rem;
  margin-bottom: 0;
  height: 100%;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  overflow-y: scroll;
  overflow-x: hidden;
}

.modal .modal-background {
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s 0.3s;
}

.modal.is-active .modal-background {
  opacity: 1;
  transition: opacity 0.3s 0.0s;
}

.modal .modal-content {
  transition: transform 0.3s 0.3s;
  transform: translate3d(100%, 0, 0);
}

.modal.is-active .modal-content {
  transform: translate3d(0, 0, 0);
  transition-delay: 0s;
}

.modal {
  display: flex;
  visibility: hidden;
  transition: visibility 0s 0.6s;
}

.modal.is-active {
  visibility: visible;
  transition: visibility 0s 0s;
}

.modal-footer {
  background-color: white;
  width: 100%;
  height: 72px;
  min-height: 72px;
  border-top: solid 1px #dddddd;
  display: flex;
  align-items: center;
  padding: 1rem;
  justify-content: space-between;
  flex-grow: 1;
  .button {
    height: 40px;
  }

  .button:first-child:not(:only-child) {
    margin-right: 8px;
  }

  .button:last-child:not(:only-child) {
    margin-left: 8px;
  }

  .button.is-primary.is-inverted {
    border-color: $primary;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/sidebar-modal.vue